import './OfficeTabs.css';
import { SegmentedControl, type SegmentedOption } from '../../components/SegmentedControl';
import { type CopyKey, t } from '../../copy/strings';
import { governorsCounting } from '../../model/governors';
import type { ResultsView, RouteOffice } from '../../model/types';
import { getOneKeyShortcuts, setOffice } from '../../state/actions';
import { useIsSimulated, useOffices, useRoute, useTvOn, useView } from '../../state/hooks';

/** `header`: dentro do TopBar (desktop e TV). `bar`: barra própria do celular (flow fixa, tablet, land). */
export type OfficeTabsVariant = 'header' | 'bar';

const LABEL: Record<RouteOffice, CopyKey> = {
  president: 'tabs.president',
  governor: 'tabs.governors',
  senate: 'tabs.senate',
  chamber: 'tabs.chamber',
};

/** Disputas de governador ainda sem definição (§11.1.1: `racePhase(uf) !== 'decided'`; model/governors.ts). */
export { governorsCounting };

/** Ponto "em apuração" na aba Governadores: presidência definida e algum governador apurando (§6.3 bloco 3). */
export function showGovernorDot(v: ResultsView | null): boolean {
  if (!v) return false;
  const presidentDecided = !!(v.national.mathDecision || v.national.tseResult);
  return presidentDecided && governorsCounting(v);
}

/**
 * Opções do grupo na ordem fixa de §5.4.1; atalhos `1`–`4` (§5.7), sem `aria-keyshortcuts` quando os atalhos de uma
 * tecla estão desligados (§15.2.7, X1); abas do arquivo com o `title` do 1º turno.
 */
export function officeOptions(
  offices: readonly RouteOffice[],
  dot: boolean,
  shortcuts = true,
): SegmentedOption<RouteOffice>[] {
  return offices.map((office, i): SegmentedOption<RouteOffice> => {
    const base = {
      id: office,
      label: t(LABEL[office]),
      keyshortcut: shortcuts ? String(i + 1) : undefined,
      title: office === 'senate' || office === 'chamber' ? t('header.tabs.archiveTitle') : undefined,
    };
    return office === 'governor' && dot ? { ...base, badge: 'live', badgeLabel: t('tabs.counting') } : base;
  });
}

export interface OfficeTabsProps {
  variant: OfficeTabsVariant;
}

/**
 * Abas de cargo (§6.3): `SegmentedControl` `capsule` com `role="group"` "Cargo" e `<button aria-pressed>`. Com menos de
 * 2 cargos não renderiza nada (nem a barra). Na TV, visível e inerte (`inert` + botões fora da ordem de Tab). O clique
 * chama `setOffice` (§5.4.2); clicar na aba ativa não faz nada.
 */
export function OfficeTabs({ variant }: OfficeTabsProps) {
  const offices = useOffices();
  const route = useRoute();
  const view = useView();
  const tv = useTvOn();
  const sim = useIsSimulated();
  if (offices.length < 2) return null;
  const options = officeOptions(offices, showGovernorDot(view), getOneKeyShortcuts());
  return (
    <div
      class={`office-tabs office-tabs--${variant}`}
      data-testid="office-tabs"
      data-sim={sim ? '' : undefined}
      inert={tv || undefined}
    >
      <SegmentedControl<RouteOffice>
        options={options}
        value={route.office}
        onChange={setOffice}
        ariaLabel={t('tabs.group.aria')}
        variant="capsule"
        readOnly={tv}
        class="office-tabs__group"
      />
    </div>
  );
}
